import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { kpiService } from '../../services/kpiService';
import { submissionService } from '../../services/submissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { normalizeText } from '../../utils/normalizeInput';
import { formatPeriodLabel } from './periodFormat';

export function KPISubmissionForm({ submission, onSaved, onSubmitted, onClose }) {
  const readOnly = submission.status === 'submitted' || submission.status === 'approved';
  const [definitions, setDefinitions] = useState([]);
  const [rules, setRules] = useState([]);
  const [values, setValues] = useState({});
  const [note, setNote] = useState(submission.author_note || '');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [defs, scoring, existing] = await Promise.all([
          kpiService.getKpiDefinitions(submission.kpi_system_id),
          kpiService.getScoringRules(submission.kpi_system_id),
          kpiService.getSubmissionKpis(submission.id),
        ]);
        if (!active) return;
        setDefinitions(defs);
        setRules(scoring);
        const initial = {};
        existing.forEach((k) => { initial[k.kpi_definition_id] = String(k.current_value ?? ''); });
        setValues(initial);
      } catch (e) {
        if (active) setError(mapSupabaseError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submission.id, submission.kpi_system_id]);

  const targetFor = (defId) =>
    rules.find((r) => r.kpi_definition_id === defId && r.period === submission.period)?.target_value ?? 0;

  const buildItems = () =>
    definitions
      .filter((d) => values[d.id] !== undefined && values[d.id] !== '')
      .map((d) => ({
        kpi_definition_id: d.id,
        current_value: parseFloat(values[d.id]),
        target_value: Number(targetFor(d.id)),
        kpi_type: d.kpi_type,
        metric_name: d.kpi_name,
      }));

  const persist = async () => {
    await kpiService.saveSubmissionKpis(submission.id, submission.user_id, submission.period, buildItems());
    if (note !== (submission.author_note || '')) {
      await submissionService.updateDraft(submission.id, { author_note: normalizeText(note) });
    }
  };

  const handleSave = async () => {
    try { setBusy(true); setError(''); await persist(); toast.success('Draft saved'); onSaved(); }
    catch (e) { setError(mapSupabaseError(e)); } finally { setBusy(false); }
  };

  const handleSubmit = async () => {
    try { setBusy(true); setError(''); await persist(); await submissionService.submit(submission.id); toast.success('Submitted for review'); onSubmitted(); }
    catch (e) { setError(mapSupabaseError(e)); setBusy(false); }
  };

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="KPI submission">
      <h3>KPI submission</h3>
      <p className="form-label">{formatPeriodLabel(submission.period, submission.period_start, submission.period_end)}</p>
      {submission.status === 'rejected' && submission.review_comment && (
        <div className="error-message" role="alert"><strong>Reviewer note:</strong> {submission.review_comment}</div>
      )}
      {error && <div className="error-message" role="alert">{error}</div>}

      {loading ? <p className="loading-msg">Loading…</p> : (
        <>
          <table className="kpi-bundle-table">
            <thead><tr><th>KPI</th><th>Target</th><th>Your value</th></tr></thead>
            <tbody>
              {definitions.map((d) => (
                <tr key={d.id}>
                  <td>{d.kpi_name}</td>
                  <td>{targetFor(d.id)}</td>
                  <td>
                    {readOnly
                      ? <span>{values[d.id] ?? '—'}</span>
                      : <input type="number" min="0" step="any" value={values[d.id] ?? ''}
                          onChange={(e) => setValues((v) => ({ ...v, [d.id]: e.target.value }))} aria-label={`value for ${d.kpi_name}`} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="form-group">
            <label htmlFor="kpi-sub-note">Notes (optional)</label>
            <textarea id="kpi-sub-note" rows={3} maxLength={1000} value={note}
              onChange={(e) => setNote(e.target.value)} disabled={readOnly} />
          </div>

          <div className="modal-buttons">
            {!readOnly && (
              <>
                <button type="button" className="btn-secondary" onClick={handleSave} disabled={busy}>Save draft</button>
                <button type="button" className="btn-edit" onClick={handleSubmit} disabled={busy}>Submit</button>
              </>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </Modal>
  );
}
